/**
 * Local progress helpers — Sister Signal v1.1.
 *
 * Everything persists in localStorage so beta testers can use these features
 * without a backend write yet. Keys are namespaced under `ss_v11_` so we can
 * cleanly migrate to server-side persistence later without colliding.
 *
 * All getters return safe defaults; never throw on a fresh install.
 */

const PATH_KEY = "ss_v11_path";
const LESSONS_DONE_KEY = "ss_v11_lessons_done";
const FIN_CHECK_KEY = "ss_v11_fin_checklist";
const GOAL_KEY_PREFIX = "ss_v11_goal_";       // ss_v11_goal_2026-02-28
const SUGGESTED_DONE_PREFIX = "ss_v11_sug_";  // ss_v11_sug_2026-02-28

function safeGet(k) { try { return window.localStorage.getItem(k); } catch { return null; } }
function safeSet(k, v) { try { window.localStorage.setItem(k, v); } catch { /* ignore */ } }
function safeDel(k) { try { window.localStorage.removeItem(k); } catch { /* ignore */ } }

function todayKey(date = new Date()) {
  // ISO date in local timezone — yyyy-mm-dd
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// -------------------------- growth path -------------------------- //
export function getActivePath() { return safeGet(PATH_KEY) || ""; }
export function setActivePath(slug) { safeSet(PATH_KEY, slug); }
export function clearActivePath() { safeDel(PATH_KEY); }

// -------------------------- lessons done -------------------------- //
function readLessonsDone() {
  const raw = safeGet(LESSONS_DONE_KEY);
  if (!raw) return [];
  try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr : []; }
  catch { return []; }
}

export function listCompletedLessons() { return readLessonsDone(); }
export function isLessonComplete(slug) { return readLessonsDone().includes(slug); }
export function setLessonComplete(slug, value) {
  const arr = readLessonsDone();
  const has = arr.includes(slug);
  let next = arr;
  if (value && !has) next = [...arr, slug];
  if (!value && has) next = arr.filter((s) => s !== slug);
  safeSet(LESSONS_DONE_KEY, JSON.stringify(next));
}

// -------------------------- financial checklist -------------------------- //
function readFin() {
  const raw = safeGet(FIN_CHECK_KEY);
  if (!raw) return {};
  try { const obj = JSON.parse(raw); return obj && typeof obj === "object" ? obj : {}; }
  catch { return {}; }
}

export function getFinChecklist() { return readFin(); }
export function setFinItem(id, value) {
  const obj = readFin();
  obj[id] = !!value;
  safeSet(FIN_CHECK_KEY, JSON.stringify(obj));
}

// -------------------------- daily goals -------------------------- //
export function getSuggestedDone(date = new Date()) {
  return safeGet(`${SUGGESTED_DONE_PREFIX}${todayKey(date)}`) === "1";
}
export function setSuggestedDone(value, date = new Date()) {
  const k = `${SUGGESTED_DONE_PREFIX}${todayKey(date)}`;
  if (value) safeSet(k, "1"); else safeDel(k);
}

export function getCustomGoal(date = new Date()) {
  const raw = safeGet(`${GOAL_KEY_PREFIX}${todayKey(date)}`);
  if (!raw) return { text: "", done: false };
  try {
    const obj = JSON.parse(raw);
    return {
      text: typeof obj?.text === "string" ? obj.text : "",
      done: !!obj?.done,
    };
  } catch { return { text: "", done: false }; }
}

export function setCustomGoal(goal, date = new Date()) {
  const k = `${GOAL_KEY_PREFIX}${todayKey(date)}`;
  safeSet(k, JSON.stringify({
    text: typeof goal?.text === "string" ? goal.text : "",
    done: !!goal?.done,
  }));
}
